/**
 * Ticket WK2, Schritt 5 und 6: Formular für alle Angaben einer Gemeinde.
 * Es dient dem Abschluss eines Falls im Prüfeingang und der freien
 * Bearbeitung in der Gemeindeliste; beide Wege schreiben denselben Datensatz.
 */

import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { completeMunicipalityReview, saveMunicipality } from "@/lib/wahlkalender.functions";
import {
  PROPOSAL_FIELD_LABELS,
  RUNS_AGAIN_LABELS,
  RUNS_AGAIN_VALUES,
  applyProposalEntry,
  type ReviewProposalEntry,
  type RunsAgain,
} from "@/lib/wahlkalender";

import { AdminField, adminInputClass } from "@/components/admin/AdminField";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { MunicipalityRow } from "@/lib/wahlkalender.server";


export type MunicipalityEditValues = {
  firstName: string;
  lastName: string;
  postalCode: string;
  population: string;
  officeLabel: string;
  office: string;
  termStartYear: string;
  termStartMonth: string;
  termStartDay: string;
  termYears: string;
  confirmedYear: string;
  confirmedMonth: string;
  confirmedDay: string;
  runsAgain: RunsAgain;
};

export function valuesFromRow(row: MunicipalityRow): MunicipalityEditValues {
  return {
    firstName: row.firstName ?? "",
    lastName: row.lastName ?? "",
    postalCode: row.postalCode ?? "",
    population: row.population === null ? "" : String(row.population),
    officeLabel: row.officeLabel,
    office: row.office,
    termStartYear: String(row.termStartYear),
    termStartMonth: String(row.termStartMonth),
    termStartDay: row.termStartDay === null ? "" : String(row.termStartDay),
    termYears: row.termYears === null ? "" : String(row.termYears),
    confirmedYear: row.confirmedYear === null ? "" : String(row.confirmedYear),
    confirmedMonth: row.confirmedMonth === null ? "" : String(row.confirmedMonth),
    confirmedDay: row.confirmedDay === null ? "" : String(row.confirmedDay),
    runsAgain: row.runsAgain,
  };
}

export function payloadFromValues(values: MunicipalityEditValues) {
  return {
    firstName: values.firstName.trim() || null,
    lastName: values.lastName.trim() || null,
    postalCode: /^\d{5}$/u.test(values.postalCode.trim()) ? values.postalCode.trim() : null,
    population: values.population.trim() === "" ? null : Number(values.population.trim()),
    officeLabel: values.officeLabel.trim() || undefined,
    office: values.office,
    termStartYear: Number(values.termStartYear),
    termStartMonth: Number(values.termStartMonth),
    termStartDay: values.termStartDay.trim() === "" ? null : Number(values.termStartDay.trim()),
    termYears: values.termYears.trim() === "" ? null : Number(values.termYears.trim()),
    confirmedYear: values.confirmedYear.trim() === "" ? null : Number(values.confirmedYear.trim()),
    confirmedMonth:
      values.confirmedMonth.trim() === "" ? null : Number(values.confirmedMonth.trim()),
    confirmedDay: values.confirmedDay.trim() === "" ? null : Number(values.confirmedDay.trim()),
    runsAgain: values.runsAgain,
  };
}

export function MunicipalityEditForm({
  municipality,
  mode = "speichern",
  proposals = [],
  onSaved,
}: {
  municipality: MunicipalityRow;
  /** „abschließen": der Haken schreibt die Werte und schließt den Fall ab. */
  mode?: "speichern" | "abschließen";
  /** Ticket WK8: Vorschläge mit altem und vorgeschlagenem Wert. */
  proposals?: ReviewProposalEntry[];
  onSaved?: () => void;
}) {
  const saveFn = useServerFn(saveMunicipality);
  const completeFn = useServerFn(completeMunicipalityReview);
  const [values, setValues] = useState<MunicipalityEditValues>(() => valuesFromRow(municipality));
  const [taken, setTaken] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValues(valuesFromRow(municipality));
    setTaken([]);
  }, [municipality.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (key: keyof MunicipalityEditValues, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  /**
   * Ticket WK8: Der Haken übernimmt genau den vorgeschlagenen Wert in das
   * Formular. Ohne Haken wird nichts übernommen; der Haken zurückgenommen
   * stellt den eigenen Stand wieder her.
   */
  const toggleProposal = (index: number, entry: ReviewProposalEntry, checked: boolean) => {
    const original = valuesFromRow(municipality);
    setTaken((prev) => (checked ? [...prev, index] : prev.filter((i) => i !== index)));
    setValues((prev) =>
      checked
        ? applyProposalEntry(prev, entry)
        : entry.field === "name"
          ? { ...prev, firstName: original.firstName, lastName: original.lastName }
          : { ...prev, population: original.population },
    );
  };


  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (mode === "abschließen") {
        const result = await completeFn({
          data: { municipalityId: municipality.id, values: payloadFromValues(values) },
        });
        toast.success(
          result.completed > 0
            ? result.changed
              ? "Fall abgeschlossen und Werte gespeichert."
              : "Fall abgeschlossen (ohne Änderung)."
            : "Gespeichert.",
        );
      } else {
        const result = await saveFn({ data: { id: municipality.id, ...payloadFromValues(values) } });
        if (result.changed) {
          const names = Object.keys(result.changes).join(", ");
          toast.success(`Gespeichert. Geändert: ${names}`);
        } else {
          toast.success("Ohne Änderung gespeichert.");
        }
      }
      onSaved?.();
    } catch (saveError) {
      setError((saveError as Error).message);
    } finally {
      setSaving(false);
    }
  };


  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {proposals.length > 0 ? (
        <ul className="space-y-2 rounded-md border border-border p-3" data-testid="wk8-vorschlaege">
          {proposals.map((entry, index) => (
            <li key={`${entry.field}-${index}`} className="text-sm">
              <p className="font-semibold">{PROPOSAL_FIELD_LABELS[entry.field]}</p>
              <p className="text-xs text-muted-foreground">
                Unser Stand: {entry.oldValue ?? "—"} · Vorschlag: {entry.proposed ?? "kein Vorschlag"}
              </p>
              <p className="text-xs text-muted-foreground">{entry.note}</p>
              {entry.proposed === null ? null : (
                <label className="mt-1 flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={taken.includes(index)}
                    onChange={(event) => toggleProposal(index, entry, event.target.checked)}
                  />
                  Vorschlag übernehmen
                </label>
              )}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <AdminField label="Vorname der amtierenden Person">
          {(props) => (
            <input
              {...props}
              className={adminInputClass}
              value={values.firstName}
              onChange={(e) => set("firstName", e.target.value)}
            />
          )}
        </AdminField>
        <AdminField label="Nachname der amtierenden Person">
          {(props) => (
            <input
              {...props}
              className={adminInputClass}
              value={values.lastName}
              onChange={(e) => set("lastName", e.target.value)}
            />
          )}
        </AdminField>
        <AdminField label="PLZ">
          {(props) => (
            <input
              {...props}
              className={adminInputClass}
              inputMode="numeric"
              value={values.postalCode}
              onChange={(e) => set("postalCode", e.target.value)}
            />
          )}
        </AdminField>
        <AdminField label="Einwohner">
          {(props) => (
            <input
              {...props}
              className={adminInputClass}
              inputMode="numeric"
              value={values.population}
              onChange={(e) => set("population", e.target.value)}
            />
          )}
        </AdminField>
        <AdminField label="Amtszeit (Jahre, optional)">
          {(props) => (
            <input
              {...props}
              className={adminInputClass}
              inputMode="numeric"
              value={values.termYears}
              onChange={(e) => set("termYears", e.target.value)}
            />
          )}
        </AdminField>
        <AdminField label="Amtsbezeichnung">
          {(props) => (
            <input
              {...props}
              className={adminInputClass}
              value={values.officeLabel}
              onChange={(e) => set("officeLabel", e.target.value)}
            />
          )}
        </AdminField>
        <AdminField label="Amtsart">
          {() => (
            <Select value={values.office} onValueChange={(next) => set("office", next)}>
              <SelectTrigger className={adminInputClass}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="buergermeister">Bürgermeister:in</SelectItem>
                <SelectItem value="oberbuergermeister">Oberbürgermeister:in</SelectItem>
                <SelectItem value="landrat">Landrat/Landrätin</SelectItem>
              </SelectContent>
            </Select>
          )}
        </AdminField>
      </div>

      <fieldset className="space-y-3 rounded-md border border-border p-3">
        <legend className="px-1 text-xs font-semibold text-muted-foreground">Amtsantritt</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          <AdminField label="Jahr">
            {(props) => (
              <input
                {...props}
                className={adminInputClass}
                inputMode="numeric"
                value={values.termStartYear}
                onChange={(e) => set("termStartYear", e.target.value)}
              />
            )}
          </AdminField>
          <AdminField label="Monat">
            {(props) => (
              <input
                {...props}
                className={adminInputClass}
                inputMode="numeric"
                value={values.termStartMonth}
                onChange={(e) => set("termStartMonth", e.target.value)}
              />
            )}
          </AdminField>
          <AdminField label="Tag (falls bekannt)">
            {(props) => (
              <input
                {...props}
                className={adminInputClass}
                inputMode="numeric"
                value={values.termStartDay}
                onChange={(e) => set("termStartDay", e.target.value)}
              />
            )}
          </AdminField>
        </div>
      </fieldset>

      <fieldset className="space-y-3 rounded-md border border-border p-3">
        <legend className="px-1 text-xs font-semibold text-muted-foreground">
          Bestätigter Wahltermin
        </legend>
        <div className="grid gap-3 sm:grid-cols-3">
          <AdminField label="Jahr (meist leer)">
            {(props) => (
              <input
                {...props}
                className={adminInputClass}
                inputMode="numeric"
                value={values.confirmedYear}
                onChange={(e) => set("confirmedYear", e.target.value)}
              />
            )}
          </AdminField>
          <AdminField label="Monat">
            {(props) => (
              <input
                {...props}
                className={adminInputClass}
                inputMode="numeric"
                value={values.confirmedMonth}
                onChange={(e) => set("confirmedMonth", e.target.value)}
              />
            )}
          </AdminField>
          <AdminField label="Tag (falls bekannt)">
            {(props) => (
              <input
                {...props}
                className={adminInputClass}
                inputMode="numeric"
                value={values.confirmedDay}
                onChange={(e) => set("confirmedDay", e.target.value)}
              />
            )}
          </AdminField>
        </div>
      </fieldset>

      <div>
        <Label className="text-sm">Tritt erneut an</Label>
        <Select
          value={values.runsAgain}
          onValueChange={(next) => set("runsAgain", next)}
        >
          <SelectTrigger className={adminInputClass + " mt-1"}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RUNS_AGAIN_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {RUNS_AGAIN_LABELS[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={saving}>
          {saving
            ? "Wird gespeichert …"
            : mode === "abschließen"
              ? "Fall abschließen"
              : "Speichern"}
        </Button>
      </div>

    </form>
  );
}
